import React, { useState, useRef, useCallback, useEffect } from 'react';
import api from '../api/axios';
import SearchableSelect from './SearchableSelect';

// ─── Web Audio beep ──────────────────────────────────────────────────────────
export const playBeep = (frequency = 880, duration = 80, volume = 0.3) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration / 1000);
  } catch {}
};

// ─── Signal bars ─────────────────────────────────────────────────────────────
export const SignalBars = ({ strength, color = '#4ade80', size = 1 }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: size * 2 }}>
    {[1,2,3,4,5].map(b => (
      <div key={b} style={{
        width: size * 4,
        height: b * size * 3 + size * 2,
        background: b <= strength ? color : 'rgba(255,255,255,0.15)',
        borderRadius: size,
        transition: 'background 0.3s',
      }} />
    ))}
  </div>
);

// ─── Battery indicator ───────────────────────────────────────────────────────
export const Battery = ({ pct = 72 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    <div style={{
      width: 20, height: 10, border: '1.5px solid rgba(255,255,255,0.5)',
      borderRadius: 2, position: 'relative', overflow: 'visible',
    }}>
      <div style={{
        position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)',
        width: 3, height: 5, background: 'rgba(255,255,255,0.5)', borderRadius: '0 1px 1px 0',
      }} />
      <div style={{
        position: 'absolute', left: 1, top: 1, bottom: 1,
        width: `${pct}%`, background: pct > 30 ? '#4ade80' : '#ef4444',
        borderRadius: 1, transition: 'width 0.5s',
      }} />
    </div>
    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>{pct}%</span>
  </div>
);

// ─── Compact widget React component (used by AssetAudit, etc.) ───────────────
const HandheldSimulator = ({
  mode: propMode,
  locationId: propLocationId,
  onScan,
  compact = false,
}) => {
  const [mode, setMode] = useState(propMode || 'uhf');
  const [locationId, setLocationId] = useState(propLocationId || '');
  const [locations, setLocations] = useState([]);
  const [scanLog, setScanLog] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [signal, setSignal] = useState(3);
  const [searchTag, setSearchTag] = useState('');
  const [proximity, setProximity] = useState(null);
  const [session, setSession] = useState(null);
  const [qrResult, setQrResult] = useState(null);
  const [nfcResult, setNfcResult] = useState(null);
  const [scannerActive, setScannerActive] = useState(false);
  // Audit mode now uses real input (keyboard wedge OR manual typing).
  // Demo mode is OFF by default — opt-in only, with a clear visible label.
  const [demoMode, setDemoMode] = useState(false);
  const [manualTag, setManualTag] = useState('');
  const wedgeRef = useRef(null);
  const scanLogRef = useRef(null);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (propLocationId) setLocationId(propLocationId); }, [propLocationId]);
  useEffect(() => { if (propMode) setMode(propMode); }, [propMode]);

  // Animate signal
  useEffect(() => {
    const interval = setInterval(() => {
      setSignal(s => Math.max(1, Math.min(5, s + (Math.random() > 0.5 ? 1 : -1))));
    }, 900);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll log
  useEffect(() => {
    if (scanLogRef.current) scanLogRef.current.scrollTop = scanLogRef.current.scrollHeight;
  }, [scanLog]);

  const addToLog = useCallback((tag, status = 'ok', extra = '') => {
    setScanLog(prev => [...prev, { tag, status, extra, time: new Date().toLocaleTimeString() }]);
    playBeep(status === 'ok' ? 880 : 440, 80);
  }, []);

  // ── UHF Audit scan ──
  // Real mode: trigger just focuses the wedge input, which captures keyboard
  // input from a connected handheld (CX1500N keyboard emulator) or typed input.
  // The actual scanning happens when an EPC is committed (Enter / blur).
  // Demo mode (opt-in): walks the asset list to simulate a full scan run.
  const handleUHFAudit = useCallback(async () => {
    if (!locationId) { alert('Select a location first'); return; }
    if (isScanning) return;

    if (!demoMode) {
      // Real mode: focus the wedge so the next keyboard scan goes straight in
      wedgeRef.current?.focus();
      return;
    }

    // Demo mode (clearly labeled, opt-in)
    setIsScanning(true); setScanLog([]);
    try {
      const { data: assets } = await api.get('/assets', { params: { location_id: locationId, limit: 500 } });
      if (!assets.length) { addToLog('No assets in this location', 'error'); setIsScanning(false); return; }
      for (const asset of assets) {
        await new Promise(r => setTimeout(r, 280 + Math.random() * 420));
        addToLog(asset.rfid_tag, 'ok', asset.asset_name + ' (demo)');
        if (onScan) onScan(asset.rfid_tag);
      }
    } catch (err) { addToLog('Error: ' + (err.response?.data?.error || err.message), 'error'); }
    setIsScanning(false);
  }, [locationId, isScanning, addToLog, onScan, demoMode]);

  // Real-input commit: keyboard-wedge writes the EPC then sends Enter,
  // OR the user types and presses Enter. Either way, this fires.
  const commitManualScan = useCallback((rawValue) => {
    const tag = String(rawValue || '').trim();
    if (!tag) return;
    addToLog(tag, 'ok', 'manual / wedge');
    if (onScan) onScan(tag);
    setManualTag('');
    // Refocus for the next scan
    setTimeout(() => wedgeRef.current?.focus(), 30);
  }, [addToLog, onScan]);

  // Keep the wedge focused while in audit mode so handheld scans land in it
  useEffect(() => {
    if (mode !== 'audit') return;
    const id = setInterval(() => {
      const el = document.activeElement;
      const isInput = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (!isInput) wedgeRef.current?.focus();
    }, 1500);
    return () => clearInterval(id);
  }, [mode]);

  // ── UHF Raw scan ──
  const handleUHFRaw = useCallback(async () => {
    if (isScanning) return;
    setIsScanning(true);
    let sess = session;
    if (!sess) {
      try { const r = await api.post('/raw-inventory/session/start'); sess = r.data.session_id; setSession(sess); } catch {}
    }
    const count = Math.floor(Math.random() * 5) + 3;
    for (let i = 0; i < count; i++) {
      await new Promise(r => setTimeout(r, 280 + Math.random() * 500));
      const tag = `E200-${String(Math.floor(Math.random() * 9999)).padStart(4,'0')}-${String(Math.floor(Math.random() * 9999)).padStart(4,'0')}`;
      const rssi = -(Math.floor(Math.random() * 40) + 50);
      addToLog(tag, 'ok', `${rssi} dBm`);
      try { await api.post('/raw-inventory/scan', { tag_number: tag, rssi, session_id: sess }); } catch {}
    }
    setIsScanning(false);
  }, [isScanning, session, addToLog]);

  // ── Asset search / proximity ──
  const handleProximity = useCallback(async () => {
    if (!searchTag.trim()) { alert('Enter a tag to search'); return; }
    setIsScanning(true); setProximity(null);
    const distances = ['Very Close', 'Close', 'Medium', 'Far', 'Very Far'];
    const sigs = [5, 4, 3, 2, 1];
    let steps = 0;
    const iv = setInterval(async () => {
      steps++;
      const idx = Math.min(Math.floor(Math.random() * 3), 4);
      setProximity({ distance: distances[idx], signal: sigs[idx], pct: (5 - sigs[idx]) * 20 });
      setSignal(sigs[idx]);
      playBeep(200 + sigs[idx] * 120, 60, 0.12);
      if (steps >= 8) {
        clearInterval(iv);
        try {
          const { data } = await api.get('/assets', { params: { search: searchTag.trim() } });
          if (data.length) {
            setProximity({ distance: 'Close', signal: 4, pct: 75, asset: data[0] });
            addToLog(`Found: ${data[0].asset_name}`, 'ok', data[0].rfid_tag);
          } else {
            setProximity({ distance: 'Not Found', signal: 0, pct: 0 });
            addToLog(`Not found: ${searchTag}`, 'error');
          }
        } catch {}
        setIsScanning(false);
      }
    }, 400);
    scanIntervalRef.current = iv;
  }, [searchTag, addToLog]);

  // ── QR / Barcode simulate ──
  const handleQRScan = useCallback(async () => {
    if (isScanning) return;
    setIsScanning(true); setScannerActive(true); setQrResult(null);
    await new Promise(r => setTimeout(r, 1800));
    try {
      const { data: assets } = await api.get('/assets', { params: { limit: 500 } });
      if (assets.length) {
        const asset = assets[Math.floor(Math.random() * assets.length)];
        setQrResult({ type: 'QR', value: asset.barcode || asset.serial_number, asset });
        addToLog(asset.barcode || asset.serial_number, 'ok', asset.asset_name);
      } else {
        const fakeBarcode = `BC${Math.floor(Math.random() * 999999).toString().padStart(6,'0')}`;
        setQrResult({ type: 'QR', value: fakeBarcode });
        addToLog(fakeBarcode, 'ok', 'Unregistered');
      }
    } catch {}
    setScannerActive(false); setIsScanning(false);
  }, [isScanning, addToLog]);

  // ── NFC simulate ──
  const handleNFCScan = useCallback(async () => {
    if (isScanning) return;
    setIsScanning(true); setNfcResult(null);
    await new Promise(r => setTimeout(r, 1200));
    const nfcId = `04:${Array.from({length:6},()=>Math.floor(Math.random()*256).toString(16).padStart(2,'0')).join(':')}`.toUpperCase();
    try {
      const { data: assets } = await api.get('/assets', { params: { limit: 500 } });
      if (assets.length) {
        const asset = assets[Math.floor(Math.random() * assets.length)];
        setNfcResult({ uid: nfcId, tech: 'ISO14443-A', asset });
        addToLog(nfcId, 'ok', asset.asset_name);
        playBeep(660, 120, 0.4);
      } else {
        setNfcResult({ uid: nfcId, tech: 'ISO14443-A' });
        addToLog(nfcId, 'ok', 'Unknown tag');
        playBeep(660, 120, 0.4);
      }
    } catch {}
    setIsScanning(false);
  }, [isScanning, addToLog]);

  const handleTrigger = () => {
    if (mode === 'audit') handleUHFAudit();
    else if (mode === 'raw') handleUHFRaw();
    else if (mode === 'search') handleProximity();
    else if (mode === 'qr') handleQRScan();
    else if (mode === 'nfc') handleNFCScan();
  };

  const clearAll = () => { setScanLog([]); setProximity(null); setQrResult(null); setNfcResult(null); };
  const uniqueTags = [...new Set(scanLog.map(s => s.tag))];

  const W = compact ? 270 : 300;

  return (
    <div style={{
      width: W, background: 'linear-gradient(160deg,#1c1c2e,#16213e,#0f3460)',
      borderRadius: 16, padding: compact ? 12 : 16,
      boxShadow: '0 6px 24px rgba(0,0,0,0.4)', border: '2px solid #2a2a4a',
      fontFamily: "'Courier New',monospace", color: '#e0e0e0', userSelect: 'none',
    }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10,
        background:'rgba(33,150,243,0.1)', borderRadius:8, padding:'8px 10px',
        border:'1px solid rgba(33,150,243,0.2)' }}>
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'#90caf9', letterSpacing:1 }}>CX1500N</div>
          <div style={{ fontSize:9, color:'#546e7a', letterSpacing:0.5 }}>RFID HANDHELD</div>
        </div>
        <SignalBars strength={signal} color="#90caf9" size={0.8} />
      </div>

      {/* Mode selector */}
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:9, color:'#546e7a', marginBottom:3, letterSpacing:0.5 }}>MODE</div>
        <SearchableSelect value={mode} onChange={e => { setMode(e.target.value); clearAll(); }} style={{
          width:'100%', background:'#0d2447', color:'#90caf9',
          border:'1px solid rgba(33,150,243,0.3)', borderRadius:6,
          padding:'5px 8px', fontSize:11, outline:'none',
        }}>
          <option value="audit">UHF AUDIT</option>
          <option value="search">ASSET SEARCH</option>
          <option value="raw">RAW SCAN</option>
          <option value="qr">QR / BARCODE</option>
          <option value="nfc">NFC</option>
        </SearchableSelect>
      </div>

      {/* Location (audit mode) */}
      {mode === 'audit' && (
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:9, color:'#546e7a', marginBottom:3 }}>LOCATION</div>
          <SearchableSelect value={locationId} onChange={e => setLocationId(e.target.value)} style={{
            width:'100%', background:'#0d2447', color:'#e0e0e0',
            border:'1px solid rgba(255,255,255,0.15)', borderRadius:6,
            padding:'5px 8px', fontSize:11, outline:'none',
          }}>
            <option value="">-- SELECT LOCATION --</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.location_code} - {l.location_name}</option>)}
          </SearchableSelect>
        </div>
      )}

      {/* Wedge input (audit mode) — VISUALLY HIDDEN but still focusable.
          Users cannot type into this directly; the Identium Keyboard Helper
          (or any keyboard-wedge app) sends keystrokes here. Pressing Enter
          commits the buffered EPC as a real scan. */}
      {mode === 'audit' && (
        <>
          <input
            ref={wedgeRef}
            value={manualTag}
            onChange={e => setManualTag(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); commitManualScan(manualTag); }
            }}
            disabled={demoMode}
            autoComplete="off" autoCorrect="off" spellCheck={false}
            aria-hidden="true"
            tabIndex={-1}
            style={{
              // Off-screen but focusable + receives synthetic keyboard input.
              // We can't display:none — focus() would no-op then.
              position: 'absolute', left: -9999, top: -9999,
              width: 1, height: 1, opacity: 0,
              border: 'none', padding: 0, margin: 0, background: 'transparent',
            }}
          />
          {/* Compact status line so the user knows the wedge is armed */}
          <div style={{ marginBottom:8 }}>
            <div style={{
              display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'6px 10px', borderRadius:6, fontSize:10,
              background: demoMode ? '#3a2810' : '#0d2447',
              border: `1px dashed ${demoMode ? '#ff9800' : '#1565c0'}`,
              color: demoMode ? '#ff9800' : '#4ade80',
            }}>
              <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{
                  width:7, height:7, borderRadius:'50%',
                  background: demoMode ? '#ff9800' : '#4ade80',
                  boxShadow: `0 0 6px ${demoMode ? '#ff9800' : '#4ade80'}`,
                }} />
                {demoMode ? 'DEMO MODE — trigger walks asset list'
                          : 'READY — pull trigger to scan via keyboard wedge'}
              </span>
              {!demoMode && manualTag && (
                <span style={{ color:'#90caf9', fontFamily:'monospace' }}>
                  buffer: {manualTag.length}c
                </span>
              )}
            </div>
            <label style={{
              display:'flex', alignItems:'center', gap:6, marginTop:6,
              fontSize:9, color:'#90a4ae', cursor:'pointer',
            }}>
              <input type="checkbox" checked={demoMode}
                onChange={e => { setDemoMode(e.target.checked); setScanLog([]); }}
                style={{ accentColor:'#ff9800' }} />
              Enable demo mode (auto-walks asset list — for testing without a reader)
            </label>
          </div>
        </>
      )}

      {/* Tag input (search mode) */}
      {mode === 'search' && (
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:9, color:'#546e7a', marginBottom:3 }}>RFID TAG</div>
          <input value={searchTag} onChange={e => setSearchTag(e.target.value)}
            placeholder="E200-XXXX-XXXX" style={{
              width:'100%', background:'#0d2447', color:'#e0e0e0',
              border:'1px solid rgba(255,255,255,0.15)', borderRadius:6,
              padding:'5px 8px', fontSize:11, outline:'none', boxSizing:'border-box',
            }} />
        </div>
      )}

      {/* Scan log */}
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:9, color:'#546e7a', marginBottom:3, display:'flex', justifyContent:'space-between' }}>
          <span>SCAN LOG</span>
          {isScanning && <span style={{ color:'#4ade80' }}>● SCANNING…</span>}
        </div>
        <div ref={scanLogRef} style={{
          background:'#050d1a', borderRadius:6, padding:'6px 8px',
          height: compact ? 80 : 110, overflowY:'auto', fontSize:10,
          border:'1px solid rgba(255,255,255,0.08)',
        }}>
          {scanLog.length === 0
            ? <div style={{ color:'#2a3a5a', textAlign:'center', paddingTop:20 }}>No scans yet</div>
            : scanLog.map((e,i) => (
                <div key={i} style={{ color: e.status==='ok' ? '#4ade80' : '#ef4444', marginBottom:2, fontSize:10 }}>
                  <span style={{ color:'#546e7a' }}>{e.time} </span>{e.tag}
                  {e.extra && <span style={{ color:'#7986cb' }}> {e.extra}</span>}
                </div>
              ))
          }
        </div>
        <div style={{ fontSize:9, color:'#546e7a', marginTop:4 }}>
          TOTAL: <b style={{ color:'#90caf9' }}>{scanLog.length}</b> &nbsp;
          UNIQUE: <b style={{ color:'#4ade80' }}>{uniqueTags.length}</b>
        </div>
      </div>

      {/* Trigger */}
      <button onClick={handleTrigger} disabled={isScanning} style={{
        width:'100%', padding:'11px',
        background: isScanning ? 'linear-gradient(135deg,#1a5c1a,#166016)'
          : (mode === 'audit' && demoMode) ? 'linear-gradient(135deg,#e65100,#ef6c00)'
          : 'linear-gradient(135deg,#2e7d32,#388e3c)',
        color:'white', border:'none', borderRadius:8, fontSize:12, fontWeight:700,
        cursor: isScanning ? 'not-allowed' : 'pointer', letterSpacing:1, marginBottom:6,
        boxShadow: isScanning ? 'none' : '0 3px 10px rgba(46,125,50,0.4)',
      }}>
        {isScanning ? '⟳ SCANNING…'
          : mode === 'search' ? '⚡ FIND ASSET'
          : mode === 'audit' && demoMode ? '⚠ DEMO: WALK ASSETS'
          : '⚡ TRIGGER SCAN'}
      </button>

      <button onClick={clearAll} style={{
        width:'100%', padding:'6px', background:'rgba(255,255,255,0.05)',
        color:'#90a4ae', border:'1px solid rgba(255,255,255,0.1)', borderRadius:6,
        fontSize:10, cursor:'pointer', letterSpacing:0.5,
      }}>CLEAR</button>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default HandheldSimulator;
